import React, {useState, useEffect} from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
} from '@mui/material';
import zipcodes from 'zipcodes';
import StateService from '../Services/State'
import loaderGif from "./../Assests/loader.gif";
const FacilityFormDialog = ({open, handleClose, handleSave, initialData}) => {
  const initialFormData = {
    name: '',
    address: '',
    city: '',
    state_id: '',
    zip: '',
    contact: '',
    phone_number: '',
  };
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [zipError, setZipError] = useState(''); // State for ZIP code error message
  const [fieldErrors, setFieldErrors] = useState({}); // State to track field errors
  const [states, setStates] = useState([]); // State to hold states data
  const [loading, setLoading] = useState(true); // Optional loading state
  const [error, setError] = useState(null); // Optional error state

  const apiKey = process.env.REACT_APP_API_KEY;

  useEffect(() => {
     // Reset the field errors every time the dialog is opened
   setFieldErrors({});
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData(initialFormData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, open]);

  useEffect(() => {
    const fetchStatesData = async () => {
      try {
        // Fetch states data from the service
        const response = await StateService.fetchStates();
        // Check if response.data is in expected format
        if (response && Array.isArray(response.data)) {
          console.log("Fetched States Data:", response.data);
          setStates(response.data); // Set the state data
        } else {
          console.error("Fetched data is not an array:", response);
          setError("Error: States data is not in expected format.");
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching states:", error);
        setError("Failed to fetch states.");
        setLoading(false);
      }
    };

    fetchStatesData(); // Call the function to fetch states data
  }, []);

  const handleChange = (e) => {
    const {name, value} = e.target;
    console.log(`Changing ${name} to ${value}`);
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (name === 'zip') {
      validateZip (value);
    }
  };

  const validateZip = (zip) => {
    const zipRegex = /^\d{5}$/; // Regex for 5-digit ZIP code
    if (!zipRegex.test (zip)) {
      setZipError ('ZIP code must be 5 digits');
      return;
    }

    const location = zipcodes.lookup (zip);
    if (location) {
      setZipError (''); // Clear error if valid
      setFormData ((prevData) => ({
        ...prevData,
        city: location.city,
        state_id: location.state, // Automatically set state if ZIP is valid
      }));
    } else {
      setZipError ('Invalid ZIP code');
    }
  };

  const validateForm = () => {
    const errors = {};
  
    // Facility Name validation
    if (!formData.name) {
      errors.name = 'Facility Name is required';
    } else if (typeof formData.name !== 'string') {
      errors.name = 'Facility Name must be a string';
    }
  
    // Address validation
    if (!formData.address) {
      errors.address = 'Address is required';
    } else if (typeof formData.address !== 'string') {
      errors.address = 'Address must be a string';
    }
  
    // City validation
    if (!formData.city) {
      errors.city = 'City is required';
    } else if (typeof formData.city !== 'string') {
      errors.city = 'City must be a string';
    }
  
    // State validation
    if (!formData.state_id) {
      errors.state_id = 'State ID is required';
  } else if (!states.some((item) => item.state_id === formData.state_id)) {
      errors.state_id = 'State ID must be a valid option';
  }
  
    // ZIP Code validation
    if (!formData.zip) {
      errors.zip = 'ZIP Code is required';
    } else if (typeof formData.zip !== 'string' || !/^\d{5}?$/.test(formData.zip)) {
      // Check if it's a string and matches the standard ZIP code format (5 digits)
      errors.zip = 'ZIP Code must be a valid format';
    }
  
    // Contact validation
    if (!formData.contact) {
      errors.contact = 'Contact is required';
    } else if (typeof formData.contact !== 'string') {
      errors.contact = 'Contact must be a string';
    }
  
    // Phone Number validation
    if (!formData.phone_number) {
      errors.phone_number = 'Phone Number is required';
    } else if (typeof formData.phone_number !== 'string' || !/^(\d{10}|\d{3}-\d{3}-\d{4}|\d{2}-\d{2}-\d{2}-\d{2}-\d{2})$/.test(formData.phone_number)) {
      // Ensure it's a 10-digit phone number
      errors.phone_number = 'Phone Number must be a valid 10-digit number';
    }
  
    if (zipError) errors.zip = zipError; // Include zipError if it's set

    setFieldErrors(errors);

    // Return true if no errors
    return Object.keys(errors).length === 0;
  };

  const onSave = async() => {
    if (validateForm()) {
      setIsSaving(true);
      try{
        await handleSave(formData);
        handleClose();
    }catch (error){
      console.error("Error saving Facility:", error);
    }finally{
      setIsSaving(false);
    }
  }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle className="modal-header">
        {initialData ? 'Update Facility' : 'Add New Facility'}
      </DialogTitle>
      {isSaving && (
        <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        display="flex"
        justifyContent="center"
        alignItems="center"
        bgcolor="rgba(255, 255, 255, 0.8)"
        zIndex={1}
      >
        <img src={loaderGif} alt="Loading..." width={50} height={50} />
      </Box>
      )}
      <DialogContent>
        <TextField
          margin="dense"
          name="name"
          label="Facility Name"
          fullWidth
          variant="outlined"
          value={formData.name}
          onChange={handleChange}
        />
        {fieldErrors.name && <div style={{ color: 'red', fontSize: '0.875rem' }}>{fieldErrors.name}</div>}

        <TextField
          margin="dense"
          name="address"
          label="Address"
          fullWidth
          variant="outlined"
          value={formData.address}
          onChange={handleChange}
        />
        {fieldErrors.address && <div style={{ color: 'red', fontSize: '0.875rem' }}>{fieldErrors.address}</div>}

        <TextField
          margin="dense"
          name="city"
          label="City"
          fullWidth
          variant="outlined"
          value={formData.city}
          onChange={handleChange}
          disabled={zipError} // Disable if there's a ZIP error
        />
        {fieldErrors.city && <div style={{ color: 'red', fontSize: '0.875rem' }}>{fieldErrors.city}</div>}

        <FormControl fullWidth margin="dense" variant="outlined" >
          <InputLabel>State</InputLabel>
          <Select
            name="state_id"
            value={formData.state_id}
            onChange={handleChange}
            label="State"
          >
            {states.length > 0
              ? states.map((item) => (
                  <MenuItem key={item.state_id} value={item.state_id}>
                    {item.state_name || 'Unknown State'}
                  </MenuItem>
                ))
              : <MenuItem value="">No states available</MenuItem>}
          </Select>
        </FormControl>
        {fieldErrors.state_id && <div style={{ color: 'red', fontSize: '0.875rem' }}>{fieldErrors.state_id}</div>}

        <TextField
          margin="dense"
          name="zip"
          label="ZIP Code"
          fullWidth
          variant="outlined"
          value={formData.zip}
          onChange={handleChange}
        />
        {fieldErrors.zip && <div style={{ color: 'red', fontSize: '0.875rem' }}>{fieldErrors.zip}</div>}

        <TextField
          margin="dense"
          name="contact"
          label="Contact"
          fullWidth
          variant="outlined"
          value={formData.contact}
          onChange={handleChange}
        />
        {fieldErrors.contact && <div style={{ color: 'red', fontSize: '0.875rem' }}>{fieldErrors.contact}</div>}

        <TextField
          margin="dense"
          name="phone_number"
          label="Phone Number"
          fullWidth
          variant="outlined"
          value={formData.phone_number}
          onChange={handleChange}
        />
        {fieldErrors.phone_number && <div style={{ color: 'red', fontSize: '0.875rem' }}>{fieldErrors.phone_number}</div>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button onClick={onSave} color="primary" variant="contained">
          {initialData ? 'Update' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FacilityFormDialog;